import { REVENUECAT_CONFIG, DEVELOPMENT_CONFIG } from '@/utils/revenueCatConfig';

let PurchasesClass: any = null;
let purchasesInstance: any = null;
let isConfigured = false;
let currentUserId: string | null = null;

interface WebProduct {
  productId: string;
  price: number;
  priceString: string;
  title: string;
  description: string;
  currencyCode: string;
}

interface WebPackage {
  identifier: string;
  product: WebProduct;
}

interface WebOffering {
  identifier: string;
  serverDescription: string;
  availablePackages: WebPackage[];
}

interface WebOfferings {
  current: WebOffering | null;
  all: Record<string, WebOffering>;
}

interface WebCustomerInfo {
  originalAppUserId: string;
  entitlements: {
    active: Record<string, any>;
    all: Record<string, any>;
  };
}

async function loadPurchasesSDK() {
  if (PurchasesClass) return PurchasesClass;

  try {
    const module = await import('@revenuecat/purchases-js');
    const Purchases = module.Purchases || (module as any).default?.Purchases || (module as any).default;
    if (!Purchases || typeof Purchases.configure !== 'function') {
      console.error('[RevenueCatWeb] SDK loaded but Purchases.configure not found. Module keys:', Object.keys(module));
      return null;
    }
    PurchasesClass = Purchases;
    return PurchasesClass;
  } catch (error) {
    console.error('[RevenueCatWeb] Failed to load SDK:', error);
    return null;
  }
}

export const revenueCatWebService = {
  async configure(appUserId: string): Promise<boolean> {
    if (isConfigured && currentUserId === appUserId) {
      console.log('[RevenueCatWeb] Already configured for user:', appUserId);
      return true;
    }

    const apiKey = REVENUECAT_CONFIG.web.apiKey;
    if (!apiKey) {
      console.error('[RevenueCatWeb] No API key configured');
      return false;
    }

    try {
      const Purchases = await loadPurchasesSDK();
      if (!Purchases) {
        console.error('[RevenueCatWeb] SDK not available');
        return false;
      }

      purchasesInstance = Purchases.configure(apiKey, appUserId);
      isConfigured = true;
      currentUserId = appUserId;

      if (DEVELOPMENT_CONFIG.debugLogging) {
        console.log('[RevenueCatWeb] Configured successfully for user:', appUserId);
      }

      return true;
    } catch (error) {
      console.error('[RevenueCatWeb] Configuration error:', error);
      isConfigured = false;
      return false;
    }
  },

  async getOfferings(): Promise<WebOfferings | null> {
    if (!isConfigured || !purchasesInstance) {
      console.error('[RevenueCatWeb] Not configured');
      return null;
    }

    try {
      const offerings = await purchasesInstance.getOfferings();

      if (DEVELOPMENT_CONFIG.debugLogging) {
        console.log('[RevenueCatWeb] Offerings received:', offerings);
      }

      const premiumOfferingId = REVENUECAT_CONFIG.web.offeringId;
      const premiumOffering = offerings?.all?.[premiumOfferingId]
        || (offerings?.current?.identifier === premiumOfferingId ? offerings.current : null);

      if (!premiumOffering) {
        console.warn(`[RevenueCatWeb] Premium offering "${premiumOfferingId}" not found`);
        return null;
      }

      const mappedOfferings: WebOfferings = {
        current: premiumOffering ? {
          identifier: premiumOffering.identifier,
          serverDescription: premiumOffering.serverDescription || '',
          availablePackages: (premiumOffering.availablePackages || []).map((pkg: any) => ({
            identifier: pkg.identifier,
            product: {
              productId: pkg.rcBillingProduct?.identifier || pkg.identifier,
              price: pkg.rcBillingProduct?.currentPrice?.amountMicros
                ? pkg.rcBillingProduct.currentPrice.amountMicros / 1000000
                : 0,
              priceString: pkg.rcBillingProduct?.currentPrice?.formattedPrice || '',
              title: pkg.rcBillingProduct?.title || pkg.identifier,
              description: pkg.rcBillingProduct?.description || '',
              currencyCode: pkg.rcBillingProduct?.currentPrice?.currency || 'USD',
            }
          }))
        } : null,
        all: { [premiumOfferingId]: premiumOffering }
      };

      return mappedOfferings;
    } catch (error) {
      console.error('[RevenueCatWeb] Error fetching offerings:', error);
      return null;
    }
  },

  async getCustomerInfo(): Promise<WebCustomerInfo | null> {
    if (!isConfigured || !purchasesInstance) {
      console.error('[RevenueCatWeb] Not configured');
      return null;
    }

    try {
      const customerInfo = await purchasesInstance.getCustomerInfo();

      if (DEVELOPMENT_CONFIG.debugLogging) {
        console.log('[RevenueCatWeb] Customer info:', customerInfo);
      }

      return {
        originalAppUserId: customerInfo.originalAppUserId,
        entitlements: {
          active: customerInfo.entitlements?.active || {},
          all: customerInfo.entitlements?.all || {}
        }
      };
    } catch (error) {
      console.error('[RevenueCatWeb] Error getting customer info:', error);
      return null;
    }
  },

  isReady(): boolean {
    return isConfigured && purchasesInstance !== null;
  },

  getCurrentUserId(): string | null {
    return currentUserId;
  },

  async logout(): Promise<void> {
    if (purchasesInstance) {
      try {
        await purchasesInstance.logout();
      } catch (error) {
        console.warn('[RevenueCatWeb] Logout error:', error);
      }
    }
    isConfigured = false;
    currentUserId = null;
    purchasesInstance = null;
  },

  async purchase(packageIdentifier: string, customerEmail?: string): Promise<{ success: boolean; customerInfo?: WebCustomerInfo; error?: string; userCancelled?: boolean }> {
    if (!isConfigured || !purchasesInstance) {
      return { success: false, error: 'RevenueCat Web not configured' };
    }

    try {
      const offerings = await purchasesInstance.getOfferings();
      const premiumOfferingId = REVENUECAT_CONFIG.web.offeringId;
      const premiumOffering = offerings?.all?.[premiumOfferingId]
        || (offerings?.current?.identifier === premiumOfferingId ? offerings.current : null);

      if (!premiumOffering) {
        return { success: false, error: 'No premium offering available' };
      }

      const pkg = (premiumOffering.availablePackages || []).find(
        (p: any) => p.identifier === packageIdentifier
      );

      if (!pkg) {
        return { success: false, error: `Package "${packageIdentifier}" not found` };
      }

      const { customerInfo } = await purchasesInstance.purchase({
        rcPackage: pkg,
        ...(customerEmail ? { customerEmail } : {}),
      });

      const mappedInfo: WebCustomerInfo = {
        originalAppUserId: customerInfo.originalAppUserId,
        entitlements: {
          active: customerInfo.entitlements?.active || {},
          all: customerInfo.entitlements?.all || {},
        },
      };

      return { success: true, customerInfo: mappedInfo };
    } catch (error: any) {
      if (error?.errorCode === 1 || error?.message?.includes('cancelled') || error?.message?.includes('canceled')) {
        return { success: false, userCancelled: true };
      }
      console.error('[RevenueCatWeb] Purchase error:', error);
      return { success: false, error: error?.message || 'Purchase failed' };
    }
  },

  reset(): void {
    isConfigured = false;
    currentUserId = null;
    purchasesInstance = null;
  }
};
